import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import { AssemblyIdRuntype } from '../idTypes';
import {
    CalculationAssemblyPostRuntype,
    CalculationAssemblyResponseRuntype,
    RfqCalculationPDFExportResponseRuntype,
    ScenarioCombinationForCalculationRuntype,
    ScenarioCombinationForCalculationRuntypePatch,
    ScenarioCombinationForCalculationRuntypePatchResponse,
    SourcingCombinationCostsPostRuntype,
    SourcingCombinationCostsRuntype,
} from './calculationBackendTypes';
import { CostResponseRuntype, FrozenScenarioCostRuntype } from './costResponseBackendType';

// Split into groups with an explicit type: the inferred one exceeds what TypeScript can serialize.

const scenarioCombinationsEndpoints = {
    'GET /calculations/scenario-combinations': endpoint({
        description: 'Gets all the scenarios that have been submitted for calculations',
        pathParams: z.undefined(),
        queryParams: z.object({
            assembly_id: AssemblyIdRuntype.optional(),
            sourcing_scenario_id: z.string().optional(),
        }),
        requestBody: z.undefined(),
        responseBody: z.array(ScenarioCombinationForCalculationRuntype),
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),

    'PATCH /calculations/scenario-combinations': endpoint({
        description: 'Updates all the scenarios that have been submitted for calculations',
        pathParams: z.undefined(),
        queryParams: z.object({ assembly_id: AssemblyIdRuntype }),
        requestBody: z.array(ScenarioCombinationForCalculationRuntypePatch),
        responseBody: z.array(ScenarioCombinationForCalculationRuntypePatchResponse),
        actionPermission: () => 'CanUpdateCalculationForRfQ',
    }),
};

const calculationAssemblyCostsGetEndpoints = {
    'GET /calculations/calculation-assembly-costs': endpoint({
        description: 'Get the scenario combination rows',
        pathParams: z.undefined(),
        queryParams: z
            .object({
                assembly_id: AssemblyIdRuntype,
            })
            .or(
                z.object({
                    calculation_assembly_details_id: z.string(),
                }),
            ),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.array(CostResponseRuntype),
        }),
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),

    'GET /calculations/calculation-assembly-costs/export-pdf-data': endpoint({
        description: 'Returns the data required to constuct the PDF-export page',
        pathParams: z.undefined(),
        queryParams: z.object({
            rfq_id: z.string(),
        }),
        requestBody: z.undefined(),
        responseBody: RfqCalculationPDFExportResponseRuntype,
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),
};

const calculationAssemblyCostsPostEndpoints = {
    'POST /calculations/calculation-assembly-costs': endpoint({
        description: 'updates the scenario combination rows',
        pathParams: z.undefined(),
        queryParams: z.object({
            assembly_id: AssemblyIdRuntype,
        }),
        requestBody: SourcingCombinationCostsPostRuntype,
        responseBody: z.object({
            data: z.array(SourcingCombinationCostsRuntype),
        }),
        actionPermission: () => 'CanCreateCalculationForRfQ',
    }),
};

const calculationAssemblyCostsPatchEndpoints = {
    'PATCH /calculations/calculation-assembly-costs/:calculationAssemblyCostId': endpoint({
        description: 'updates the calculation assembly costs',
        pathParams: z.object({
            calculationAssemblyCostId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: SourcingCombinationCostsPostRuntype,
        responseBody: z.object({
            data: SourcingCombinationCostsRuntype,
        }),
        invalidates: ['GET /calculations/calculation-assembly-costs'],
        actionPermission: () => 'CanUpdateCalculationForRfQ',
    }),
};

const calculationAssemblyDetailsEndpoints = {
    'GET /calculations/calculation-assembly-details': endpoint({
        description: 'Gets the calculation assembly detail of an assembly',
        pathParams: z.undefined(),
        queryParams: z.object({
            assembly_id: AssemblyIdRuntype,
        }),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.array(CalculationAssemblyResponseRuntype),
        }),
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),

    'POST /calculations/calculation-assembly-details': endpoint({
        description: 'Creates a calculation assembly detail',
        pathParams: z.undefined(),
        queryParams: z.object({
            create_from_calculation_template_id: z.string().optional(),
        }),
        requestBody: CalculationAssemblyPostRuntype,
        responseBody: z.object({
            data: CalculationAssemblyResponseRuntype,
        }),
        actionPermission: () => 'CanCreateCalculationForRfQ',
    }),

    'PATCH /calculations/calculation-assembly-details/:calculationAssemblyDetailsId': endpoint({
        description: 'Update a calculation assembly detail',
        pathParams: z.object({
            calculationAssemblyDetailsId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: CalculationAssemblyResponseRuntype,
        responseBody: z.object({
            data: CalculationAssemblyResponseRuntype,
        }),
        invalidates: ['GET /calculations/calculation-assembly-costs', 'GET /calculations/calculation-assembly-details'],
        actionPermission: () => 'CanUpdateCalculationForRfQ',
    }),

    'PATCH /calculations/calculation-assembly-details/:calculationAssemblyDetailsId/reset': endpoint({
        description: 'Resets the calculation assembly detail values',
        pathParams: z.object({
            calculationAssemblyDetailsId: z.string(),
        }),
        queryParams: z.union([z.object({ reset_to_template: z.string() }), z.object({})]),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: CalculationAssemblyResponseRuntype,
        }),
        invalidates: ['GET /calculations/calculation-assembly-costs', 'GET /calculations/calculation-assembly-details'],
        actionPermission: () => 'CanUpdateCalculationForRfQ',
    }),
};

const frozenScenarioCostEndpoints = {
    'POST /calculations/frozen-scenario-cost': endpoint({
        description: 'Freezes a calculation',
        pathParams: z.undefined(),
        queryParams: z.union([z.object({ calculation_assembly_cost_id: z.string() }), z.object({})]),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: FrozenScenarioCostRuntype,
        }),
        invalidates: ['GET /calculations/calculation-assembly-costs'],
        actionPermission: () => 'CanCreateCalculationForRfQ',
    }),

    'DELETE /calculations/frozen-scenario-cost/:frozenId': endpoint({
        description: 'Unfreezes a calculation',
        pathParams: z.object({
            frozenId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            deleted: z.number(),
        }),
        invalidates: ['GET /calculations/calculation-assembly-costs'],
        actionPermission: () => 'CanDeleteCalculationForRfQ',
    }),
};

type ScenarioCombinationsEndpoints = typeof scenarioCombinationsEndpoints;
type CalculationAssemblyCostsGetEndpoints = typeof calculationAssemblyCostsGetEndpoints;
type CalculationAssemblyCostsPostEndpoints = typeof calculationAssemblyCostsPostEndpoints;
type CalculationAssemblyCostsPatchEndpoints = typeof calculationAssemblyCostsPatchEndpoints;
type CalculationAssemblyDetailsEndpoints = typeof calculationAssemblyDetailsEndpoints;
type FrozenScenarioCostEndpoints = typeof frozenScenarioCostEndpoints;

interface CalculationEndpoints
    extends
        ScenarioCombinationsEndpoints,
        CalculationAssemblyCostsGetEndpoints,
        CalculationAssemblyCostsPostEndpoints,
        CalculationAssemblyCostsPatchEndpoints,
        CalculationAssemblyDetailsEndpoints,
        FrozenScenarioCostEndpoints {}

export const calculationEndpoints: CalculationEndpoints = {
    ...scenarioCombinationsEndpoints,
    ...calculationAssemblyCostsGetEndpoints,
    ...calculationAssemblyCostsPostEndpoints,
    ...calculationAssemblyCostsPatchEndpoints,
    ...calculationAssemblyDetailsEndpoints,
    ...frozenScenarioCostEndpoints,
};
